import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePrevious } from 'react-use';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useReportError } from '@/hooks/useReportError';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { useUnloadPromptContext } from '@/navigation/unloadPrompt.context';
import { useBalanceSheetTableContext, ViewConfiguration } from '../../BalanceSheetTable/BalanceSheetTableContext';
import { BalanceSheetConfigColumn, RefetchQueryDescriptor, UpdateConfigurationFn } from '../BalanceSheetConfigPanel.types';
import { useBalanceSheetConfigSuggestions } from './useBalanceSheetConfigSuggestions';
import { useDraftColumnOrder, UseDraftColumnOrderReturn } from './useDraftColumnOrder';
import { useDraftConfig, UseDraftConfigReturn } from './useDraftConfig';
import { useDraftHiddenColumns, UseDraftHiddenColumnsReturn } from './useDraftHiddenColumns';
export interface UseBalanceSheetConfigPanelStateArgs {
  open: boolean;
  saveOnDone?: boolean;
  /**
   * Called when the panel closes. An optional `restoredConfig` is passed
   * during cancel-with-revert so the caller can sync the correct
   * (pre-edit) config to form state instead of reading from the
   * stale closure value of `viewConfiguration`.
   */
  onClose: (restoredConfig?: ViewConfiguration) => void;
  inEstateColumns: BalanceSheetConfigColumn[];
  inEstateTotalColumn: BalanceSheetConfigColumn | null;
  outOfEstateColumns: BalanceSheetConfigColumn[];
  outOfEstateTotalColumn: BalanceSheetConfigColumn | null;
  /** Persist a configuration update to the backend */
  updateConfiguration: UpdateConfigurationFn;
  /** Queries to refetch after a mutation */
  refetchDocuments: RefetchQueryDescriptor[];
}
export function useBalanceSheetConfigPanelState(t0) {
  const $ = _c(100);
  const {
    open,
    saveOnDone: t1,
    onClose,
    inEstateColumns,
    inEstateTotalColumn,
    outOfEstateColumns,
    outOfEstateTotalColumn,
    updateConfiguration,
    refetchDocuments
  } = t0;
  const saveOnDone = t1 === undefined ? false : t1;
  const {
    viewConfiguration,
    viewConfigurationId,
    setViewConfiguration,
    setDraftColumnOrder,
    setDraftHiddenColumns
  } = useBalanceSheetTableContext();
  const trackUserEvent = useTrackUserEvent();
  const {
    reportError
  } = useReportError();
  const {
    showFeedback
  } = useFeedback();
  let t2;
  if ($[0] !== inEstateColumns || $[1] !== outOfEstateColumns) {
    t2 = [...inEstateColumns, ...outOfEstateColumns];
    $[0] = inEstateColumns;
    $[1] = outOfEstateColumns;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const allColumns = t2;
  let t3;
  if ($[3] !== refetchDocuments || $[4] !== reportError || $[5] !== saveOnDone || $[6] !== setViewConfiguration || $[7] !== showFeedback || $[8] !== updateConfiguration || $[9] !== viewConfigurationId) {
    t3 = {
      viewConfigurationId,
      saveOnDone,
      setViewConfiguration,
      updateConfiguration,
      refetchDocuments,
      reportError,
      showFeedback
    };
    $[3] = refetchDocuments;
    $[4] = reportError;
    $[5] = saveOnDone;
    $[6] = setViewConfiguration;
    $[7] = showFeedback;
    $[8] = updateConfiguration;
    $[9] = viewConfigurationId;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const draftConfig = useDraftConfig(t3);
  let t4;
  if ($[11] !== inEstateColumns || $[12] !== inEstateTotalColumn || $[13] !== outOfEstateColumns || $[14] !== outOfEstateTotalColumn || $[15] !== setDraftColumnOrder) {
    t4 = {
      inEstateColumns,
      outOfEstateColumns,
      inEstateTotalColumn,
      outOfEstateTotalColumn,
      setDraftColumnOrder
    };
    $[11] = inEstateColumns;
    $[12] = inEstateTotalColumn;
    $[13] = outOfEstateColumns;
    $[14] = outOfEstateTotalColumn;
    $[15] = setDraftColumnOrder;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  const columnOrder = useDraftColumnOrder(t4);
  let t5;
  if ($[17] !== setDraftHiddenColumns) {
    t5 = {
      setDraftHiddenColumns
    };
    $[17] = setDraftHiddenColumns;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const hiddenCols = useDraftHiddenColumns(t5);
  let t6;
  if ($[19] !== draftConfig.applyConfig || $[20] !== draftConfig.config || $[21] !== trackUserEvent) {
    t6 = {
      localConfig: draftConfig.config,
      applyConfig: draftConfig.applyConfig,
      trackUserEvent
    };
    $[19] = draftConfig.applyConfig;
    $[20] = draftConfig.config;
    $[21] = trackUserEvent;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  const {
    applyConfigPreset,
    undoLastChange,
    configHistoryLength,
    appliedSuggestions,
    isSuggestionThinking,
    resetSuggestions
  } = useBalanceSheetConfigSuggestions(t6);
  const [showDiscardConfirmation, setShowDiscardConfirmation] = useState(false);
  let t7;
  if ($[23] !== allColumns || $[24] !== columnOrder || $[25] !== draftConfig || $[26] !== hiddenCols || $[27] !== resetSuggestions) {
    t7 = config => {
      draftConfig.reinitialize(config);
      columnOrder.reinitialize();
      hiddenCols.reinitialize(allColumns);
      resetSuggestions();
      setShowDiscardConfirmation(false);
    };
    $[23] = allColumns;
    $[24] = columnOrder;
    $[25] = draftConfig;
    $[26] = hiddenCols;
    $[27] = resetSuggestions;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  const reinitializePanel = t7;
  const prevOpen = usePrevious(open);
  let t8;
  let t9;
  if ($[29] !== open || $[30] !== prevOpen || $[31] !== reinitializePanel || $[32] !== viewConfiguration) {
    t8 = () => {
      if (open && !prevOpen) {
        reinitializePanel(viewConfiguration);
      }
    };
    t9 = [open, prevOpen, viewConfiguration, reinitializePanel];
    $[29] = open;
    $[30] = prevOpen;
    $[31] = reinitializePanel;
    $[32] = viewConfiguration;
    $[33] = t8;
    $[34] = t9;
  } else {
    t8 = $[33];
    t9 = $[34];
  }
  useEffect(t8, t9);
  const hasUnsavedChanges = draftConfig.isDirty || columnOrder.isDirty || hiddenCols.isDirty;
  const {
    setShouldBlockNavCtx
  } = useUnloadPromptContext();
  let t10;
  let t11;
  if ($[35] !== hasUnsavedChanges || $[36] !== setShouldBlockNavCtx) {
    t10 = () => {
      setShouldBlockNavCtx(hasUnsavedChanges);
      return () => setShouldBlockNavCtx(false);
    };
    t11 = [hasUnsavedChanges, setShouldBlockNavCtx];
    $[35] = hasUnsavedChanges;
    $[36] = setShouldBlockNavCtx;
    $[37] = t10;
    $[38] = t11;
  } else {
    t10 = $[37];
    t11 = $[38];
  }
  useEffect(t10, t11);
  let t12;
  if ($[39] !== columnOrder || $[40] !== draftConfig.config || $[41] !== hiddenCols || $[42] !== refetchDocuments || $[43] !== reportError || $[44] !== showFeedback || $[45] !== trackUserEvent || $[46] !== updateConfiguration || $[47] !== viewConfigurationId) {
    t12 = () => {
      if (!draftConfig.config) {
        return;
      }
      const columnOrderIds = columnOrder.getColumnOrderForSave();
      updateConfiguration({
        id: viewConfigurationId,
        update: {
          ...draftConfig.config,
          columnOrder: columnOrderIds,
          hiddenColumns: Array.from(hiddenCols.hiddenColumns),
          hideSmallValues: false
        },
        refetchQueries: refetchDocuments,
        awaitRefetchQueries: true
      }).catch(error => {
        reportError("Failed to save configuration", error as Error);
        showFeedback("Failed to save configuration");
      }).finally(() => {
        columnOrder.clearDraft();
        hiddenCols.clearDraft();
      });
      trackUserEvent("balance_sheet configuration updated", {
        columnCount: columnOrderIds.length
      });
      columnOrder.commitSnapshot();
      hiddenCols.commitSnapshot();
    };
    $[39] = columnOrder;
    $[40] = draftConfig.config;
    $[41] = hiddenCols;
    $[42] = refetchDocuments;
    $[43] = reportError;
    $[44] = showFeedback;
    $[45] = trackUserEvent;
    $[46] = updateConfiguration;
    $[47] = viewConfigurationId;
    $[48] = t12;
  } else {
    t12 = $[48];
  }
  const persistColumnChanges = t12;
  let t13;
  if ($[49] !== draftConfig || $[50] !== onClose || $[51] !== persistColumnChanges) {
    t13 = () => {
      persistColumnChanges();
      draftConfig.commitSnapshot();
      onClose(draftConfig.config ?? undefined);
    };
    $[49] = draftConfig;
    $[50] = onClose;
    $[51] = persistColumnChanges;
    $[52] = t13;
  } else {
    t13 = $[52];
  }
  const handleSave = t13;
  let t14;
  if ($[53] !== columnOrder || $[54] !== draftConfig || $[55] !== hiddenCols || $[56] !== onClose || $[57] !== resetSuggestions) {
    t14 = () => {
      const original = draftConfig.revert();
      hiddenCols.revert();
      columnOrder.reinitialize();
      resetSuggestions();
      setShowDiscardConfirmation(false);
      onClose(original ?? undefined);
    };
    $[53] = columnOrder;
    $[54] = draftConfig;
    $[55] = hiddenCols;
    $[56] = onClose;
    $[57] = resetSuggestions;
    $[58] = t14;
  } else {
    t14 = $[58];
  }
  const executeCancel = t14;
  let t15;
  if ($[59] !== draftConfig || $[60] !== hasUnsavedChanges || $[61] !== hiddenCols || $[62] !== onClose || $[63] !== persistColumnChanges) {
    t15 = () => {
      if (hasUnsavedChanges) {
        persistColumnChanges();
      }
      draftConfig.commitSnapshot();
      hiddenCols.commitSnapshot();
      onClose(draftConfig.config ?? undefined);
    };
    $[59] = draftConfig;
    $[60] = hasUnsavedChanges;
    $[61] = hiddenCols;
    $[62] = onClose;
    $[63] = persistColumnChanges;
    $[64] = t15;
  } else {
    t15 = $[64];
  }
  const handleDone = t15;
  let t16;
  if ($[65] !== hasUnsavedChanges || $[66] !== onClose) {
    t16 = () => {
      if (hasUnsavedChanges) {
        setShowDiscardConfirmation(true);
      } else {
        onClose();
      }
    };
    $[65] = hasUnsavedChanges;
    $[66] = onClose;
    $[67] = t16;
  } else {
    t16 = $[67];
  }
  const handleCancel = t16;
  let t17;
  if ($[68] !== executeCancel) {
    t17 = reason => {
      if (reason === "cancel") {
        executeCancel();
      } else {
        setShowDiscardConfirmation(false);
      }
    };
    $[68] = executeCancel;
    $[69] = t17;
  } else {
    t17 = $[69];
  }
  const handleDiscardConfirmation = t17;
  let t18;
  if ($[70] !== draftConfig) {
    t18 = field => _event => {
      if (!draftConfig.config) {
        return;
      }
      draftConfig.updateField(field, !draftConfig.config[field]);
    };
    $[70] = draftConfig;
    $[71] = t18;
  } else {
    t18 = $[71];
  }
  const handleSwitchChange = t18;
  let t19;
  if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = new Set(["showSummaryMetrics", "showExpandedAssetCategories"]);
    $[72] = t19;
  } else {
    t19 = $[72];
  }
  const DISPLAY_ONLY_FIELDS = t19;
  let t20;
  if ($[73] !== draftConfig) {
    t20 = field_0 => event => {
      if (DISPLAY_ONLY_FIELDS.has(field_0)) {
        draftConfig.updateLocal({
          [field_0]: event.target.checked
        });
      } else {
        draftConfig.updateField(field_0, event.target.checked);
      }
    };
    $[73] = draftConfig;
    $[74] = t20;
  } else {
    t20 = $[74];
  }
  const handleCheckboxChange = t20;
  let t21;
  if ($[75] !== draftConfig) {
    t21 = value => draftConfig.updateLocal({
      displayName: value
    });
    $[75] = draftConfig;
    $[76] = t21;
  } else {
    t21 = $[76];
  }
  let t22;
  if ($[77] !== appliedSuggestions || $[78] !== applyConfigPreset || $[79] !== columnOrder.handleInEstateDropped || $[80] !== columnOrder.handleOutOfEstateDropped || $[81] !== columnOrder.orderedInEstate || $[82] !== columnOrder.orderedOutOfEstate || $[83] !== configHistoryLength || $[84] !== draftConfig.config || $[85] !== executeCancel || $[86] !== handleCancel || $[87] !== handleCheckboxChange || $[88] !== handleDiscardConfirmation || $[89] !== handleDone || $[90] !== handleSave || $[91] !== handleSwitchChange || $[92] !== hasUnsavedChanges || $[93] !== hiddenCols.hiddenColumns || $[94] !== hiddenCols.toggleVisibility || $[95] !== isSuggestionThinking || $[96] !== showDiscardConfirmation || $[97] !== t21 || $[98] !== undoLastChange) {
    t22 = {
      localConfig: draftConfig.config,
      hiddenColumns: hiddenCols.hiddenColumns,
      hasUnsavedChanges,
      showDiscardConfirmation,
      orderedInEstate: columnOrder.orderedInEstate,
      orderedOutOfEstate: columnOrder.orderedOutOfEstate,
      handleSwitchChange,
      handleCheckboxChange,
      handleDisplayNameChange: t21,
      handleColumnVisibilityChange: hiddenCols.toggleVisibility,
      handleInEstateDropped: columnOrder.handleInEstateDropped,
      handleOutOfEstateDropped: columnOrder.handleOutOfEstateDropped,
      handleSave,
      handleCancel,
      executeCancel,
      handleDone,
      handleDiscardConfirmation,
      applyConfigPreset,
      undoLastChange,
      configHistoryLength,
      appliedSuggestions,
      isSuggestionThinking
    };
    $[77] = appliedSuggestions;
    $[78] = applyConfigPreset;
    $[79] = columnOrder.handleInEstateDropped;
    $[80] = columnOrder.handleOutOfEstateDropped;
    $[81] = columnOrder.orderedInEstate;
    $[82] = columnOrder.orderedOutOfEstate;
    $[83] = configHistoryLength;
    $[84] = draftConfig.config;
    $[85] = executeCancel;
    $[86] = handleCancel;
    $[87] = handleCheckboxChange;
    $[88] = handleDiscardConfirmation;
    $[89] = handleDone;
    $[90] = handleSave;
    $[91] = handleSwitchChange;
    $[92] = hasUnsavedChanges;
    $[93] = hiddenCols.hiddenColumns;
    $[94] = hiddenCols.toggleVisibility;
    $[95] = isSuggestionThinking;
    $[96] = showDiscardConfirmation;
    $[97] = t21;
    $[98] = undoLastChange;
    $[99] = t22;
  } else {
    t22 = $[99];
  }
  return t22;
}